// client/src/lib/imaController.js
const IMA_SRC = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";

function ensureImaScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.ima) {
      resolve(window.google.ima);
      return;
    }

    const existing = document.querySelector(`script[src="${IMA_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => {
        if (window.google?.ima) resolve(window.google.ima);
        else reject(new Error("IMA script loaded but google.ima missing"));
      });
      existing.addEventListener("error", () => {
        reject(new Error("Failed to load existing IMA script"));
      });
      return;
    }

    const script = document.createElement("script");
    script.src = IMA_SRC;
    script.async = true;

    script.onload = () => {
      if (window.google?.ima) resolve(window.google.ima);
      else reject(new Error("IMA script loaded but google.ima missing"));
    };

    script.onerror = () => {
      reject(new Error("Failed to load IMA SDK"));
    };

    document.head.appendChild(script);
  });
}

export class ImaController {
  constructor({
    videoEl,
    adContainerEl,
    adContainerId,
    adTagUrl,
    onLog,
    onAdBreakStart,
    onAdBreakEnd,
    willAutoPlay = false,
    willPlayMuted = false,
  }) {
    this.videoEl = videoEl || null;
    this.adContainerEl =
      adContainerEl ||
      (adContainerId ? document.getElementById(adContainerId) : null) ||
      null;

    this.adTagUrl = adTagUrl;
    this.onLog = onLog || (() => {});
    this.onAdBreakStart = onAdBreakStart || (() => {});
    this.onAdBreakEnd = onAdBreakEnd || (() => {});
    this.willAutoPlay = !!willAutoPlay;
    this.willPlayMuted = !!willPlayMuted;
    this._destroyed = false;

    this.adsLoader = null;
    this.adsManager = null;
    this.adDisplayContainer = null;

    this._requestInFlight = false;
    this._adsPlaying = false;
    this._resolveAd = null;
    this._adEnded = false;
    this._displayInitialized = false;
    this._requestedMuted = this.willPlayMuted;

    this._boundResize = null;

    try {
      if (this.adContainerEl) this.adContainerEl.style.pointerEvents = "none";
    } catch {}

    this._bindResizeHandler();
  }

  _bindResizeHandler() {
    this._boundResize = () => {
      if (this._destroyed || !this.adsManager || !this.adContainerEl) return;

      try {
        const ima = window.google?.ima;
        if (!ima) return;

        const w = this.adContainerEl.offsetWidth || 640;
        const h = this.adContainerEl.offsetHeight || 360;
        this.adsManager.resize(w, h, ima.ViewMode.NORMAL);
      } catch (e) {
        this.onLog("[IMA] resize error", e);
      }
    };

    window.addEventListener("resize", this._boundResize);
  }

  _cleanupAdUi() {
    try {
      if (this.adContainerEl) {
        this.adContainerEl.style.pointerEvents = "none";
        this.adContainerEl.innerHTML = "";
      }
    } catch {}
  }

  _safeResumeContent() {
    try {
      this.videoEl?.play?.().catch(() => {});
    } catch {}
  }

  _safeDestroyAdsManager() {
    try {
      this.adsManager?.stop?.();
    } catch {}
    try {
      this.adsManager?.destroy?.();
    } catch {}
    this.adsManager = null;
  }

  _finishAd(success) {
    const done = this._resolveAd;
    this._resolveAd = null;
    this._requestInFlight = false;

    if (done) done(!!success);
  }

  _endAdSession(success) {
    if (this._adEnded) return;
    this._adEnded = true;

    this._adsPlaying = false;
    this._cleanupAdUi();
    this._safeDestroyAdsManager();

    try {
      this.onAdBreakEnd(!!success);
    } catch (e) {
      this.onLog("[IMA] onAdBreakEnd callback error", e);
    }

    this._finishAd(success);
    this._safeResumeContent();
  }

  async init() {
    this.onLog("[IMA] init start");

    const ima = await ensureImaScript();
    if (this._destroyed) return;

    if (!ima) throw new Error("IMA SDK not available after load");
    if (!this.videoEl) throw new Error("ImaController: missing videoEl");
    if (!this.adContainerEl) {
      throw new Error("ImaController: missing adContainerEl/adContainerId");
    }

    this.onLog("[IMA] sdk ready");
    this.onLog("[IMA] init with video/container", {
      hasVideoEl: !!this.videoEl,
      hasAdContainerEl: !!this.adContainerEl,
    });

    this.adDisplayContainer = new ima.AdDisplayContainer(
      this.adContainerEl,
      this.videoEl
    );

    this.adsLoader = new ima.AdsLoader(this.adDisplayContainer);

    this.adsLoader.addEventListener(
      ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      (e) => this._onAdsManagerLoaded(e),
      false
    );

    this.adsLoader.addEventListener(
      ima.AdErrorEvent.Type.AD_ERROR,
      (e) => this._onAdError(e),
      false
    );

    this.onLog("[IMA] init complete");
  }

  initializeForUserGesture() {
    if (this._destroyed || this._displayInitialized) return;

    try {
      this.adDisplayContainer?.initialize();
      this._displayInitialized = true;
      this.onLog("[IMA] adDisplayContainer.initialize() ok");
    } catch (e) {
      this.onLog("[IMA] initializeForUserGesture failed", e);
    }
  }

  setVolume(volume = 1) {
    const numericVolume = Number(volume);
    const safeVolume = Number.isFinite(numericVolume)
      ? Math.min(1, Math.max(0, numericVolume))
      : 1;

    let applied = false;

    try {
      if (this.adsManager?.setVolume) {
        this.adsManager.setVolume(safeVolume);
        applied = true;
      }
    } catch (e) {
      this.onLog("[IMA] adsManager volume change failed", e);
    }

    try {
      if (this.videoEl) {
        this.videoEl.volume = safeVolume;
        this.videoEl.defaultMuted = safeVolume === 0;
        this.videoEl.muted = safeVolume === 0;
        applied = true;
      }
    } catch (e) {
      this.onLog("[IMA] content video volume change failed", e);
    }

    this._requestedMuted = safeVolume === 0;
    return applied;
  }

  isMuted() {
    try {
      if (this.adsManager?.getVolume) {
        return Number(this.adsManager.getVolume()) <= 0;
      }
    } catch {
      // Fall back to the requested playback mode.
    }

    return this._requestedMuted;
  }

  requestAndPlayAd(customTagUrl, playbackOptions = {}) {
    if (this._destroyed) return Promise.resolve(false);
    if (!this.adsLoader || !this.videoEl || !this.adContainerEl) {
      this.onLog("[IMA] request skipped: loader/video/container missing");
      return Promise.resolve(false);
    }

    if (this._requestInFlight || this._adsPlaying) {
      this.onLog("[IMA] request skipped: already in flight/playing");
      return Promise.resolve(false);
    }

    this._requestInFlight = true;
    this._adEnded = false;

    return new Promise((resolve) => {
      this._resolveAd = resolve;

      const ima = window.google?.ima;
      if (!ima) {
        this.onLog("[IMA] request skipped: google.ima missing");
        this._requestInFlight = false;
        return resolve(false);
      }

      const tag =
        customTagUrl ||
        (typeof this.adTagUrl === "function" ? this.adTagUrl() : this.adTagUrl);

      if (!tag || typeof tag !== "string") {
        this.onLog("[IMA] request skipped: invalid tag", tag);
        this._requestInFlight = false;
        return resolve(false);
      }

      const req = new ima.AdsRequest();
      req.adTagUrl = tag;

      const willAutoPlay =
        playbackOptions.autoPlay == null
          ? this.willAutoPlay
          : !!playbackOptions.autoPlay;
      const willPlayMuted =
        playbackOptions.muted == null
          ? this.willPlayMuted
          : !!playbackOptions.muted;

      this._requestedMuted = willPlayMuted;

      try {
        req.setAdWillAutoPlay?.(willAutoPlay);
        req.setAdWillPlayMuted?.(willPlayMuted);
      } catch (e) {
        this.onLog("[IMA] autoplay flags unavailable", e);
      }

      const w = this.adContainerEl.offsetWidth || 640;
      const h = this.adContainerEl.offsetHeight || 360;

      req.linearAdSlotWidth = w;
      req.linearAdSlotHeight = h;
      req.nonLinearAdSlotWidth = w;
      req.nonLinearAdSlotHeight = h;

      this.initializeForUserGesture();

      try {
        this.videoEl.pause();
      } catch {}

      this.onLog("[IMA] requesting ad...", tag);

      try {
        this.adsLoader.requestAds(req);
      } catch (e) {
        this.onLog("[IMA] requestAds threw", e);
        this._finishAd(false);
        this._safeResumeContent();
      }
    });
  }

  requestAndPlayPreroll(playbackOptions = {}) {
    return this.requestAndPlayAd(undefined, playbackOptions);
  }

  _onAdsManagerLoaded(event) {
    const ima = window.google?.ima;
    if (!ima || this._destroyed) return;

    this.onLog("[IMA] ADS_MANAGER_LOADED");

    try {
      this.adsManager = event.getAdsManager(this.videoEl);
    } catch (e) {
      this.onLog("[IMA] getAdsManager failed", e);
      this._endAdSession(false);
      return;
    }

    this.adsManager.addEventListener(
      ima.AdErrorEvent.Type.AD_ERROR,
      (e) => this._onAdError(e)
    );

    this.adsManager.addEventListener(
      ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      () => {
        this.onLog("[IMA] CONTENT_PAUSE_REQUESTED");
        this._adsPlaying = true;
        try {
          this.onAdBreakStart();
        } catch (e) {
          this.onLog("[IMA] onAdBreakStart callback error", e);
        }
        try {
          if (this.adContainerEl) this.adContainerEl.style.pointerEvents = "auto";
        } catch {}
        try {
          this.videoEl.pause();
        } catch {}
      }
    );

    this.adsManager.addEventListener(
      ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      () => {
        this.onLog("[IMA] CONTENT_RESUME_REQUESTED");
        this._endAdSession(true);
      }
    );

    this.adsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
      this.onLog("[IMA] ALL_ADS_COMPLETED");
      this._endAdSession(true);
    });

    if (ima.AdEvent.Type.SKIPPED) {
      this.adsManager.addEventListener(ima.AdEvent.Type.SKIPPED, () => {
        this.onLog("[IMA] SKIPPED");
        this._endAdSession(true);
      });
    }

    if (ima.AdEvent.Type.USER_CLOSE) {
      this.adsManager.addEventListener(ima.AdEvent.Type.USER_CLOSE, () => {
        this.onLog("[IMA] USER_CLOSE");
        this._endAdSession(true);
      });
    }

    try {
      const w = this.adContainerEl.offsetWidth || 640;
      const h = this.adContainerEl.offsetHeight || 360;

      this.adsManager.init(w, h, ima.ViewMode.NORMAL);
      this.setVolume(this._requestedMuted ? 0 : 1);
      this.adsManager.start();
      this.onLog(
        "[IMA] adsManager started",
        this._requestedMuted ? "muted" : "with sound"
      );
    } catch (e) {
      this.onLog("[IMA] adsManager start failed", e);
      this._endAdSession(false);
    }
  }

  _onAdError(event) {
    if (this._destroyed) return;

    const err = event?.getError ? event.getError() : event;
    this.onLog("[IMA] AD_ERROR:", err?.toString?.() || err || "unknown");
    this._endAdSession(false);
  }

  destroy() {
    this._destroyed = true;

    if (this._boundResize) {
      window.removeEventListener("resize", this._boundResize);
      this._boundResize = null;
    }

    this._cleanupAdUi();
    this._safeDestroyAdsManager();

    try {
      this.adsLoader?.contentComplete?.();
    } catch {}
    this.adsLoader = null;

    this.adDisplayContainer = null;
    this._displayInitialized = false;
    this._requestInFlight = false;
    this._adsPlaying = false;
    this._resolveAd = null;
    this._adEnded = false;
    this._requestedMuted = this.willPlayMuted;
  }
}

